import {fetchPoll} from '@github-ui/fetch-utils'

import type {AheadBehind, NetworkGraphTableRow} from './types'

/**
 * Raw shape of a row as returned by the `network/table` endpoint (snake_case). `ahead_behind` is
 * an `[ahead, behind]` pair, and is `null` (together with its description) for heads Git cannot
 * compare against the default branch and for the default branch row itself, which the endpoint
 * flags with `is_default_branch` rather than comparing against itself; the source and commit-date
 * fields can also be `null` when the referenced commit is outside the returned commit set.
 */
interface NetworkTableApiRow {
  repo: string
  branch: string
  ahead_behind: number[] | null
  ahead_behind_description: string | null
  ahead_path?: string | null
  behind_path?: string | null
  commit_date: string | null
  commit_description: string | null
  commit: string
  commit_author: string | null
  commit_author_login: string | null
  commit_author_avatar_url: string | null
  source_repo: string | null
  source_branch: string | null
  source_date: string | null
  source_commit_description: string | null
  source_commit: string | null
  source_commit_author: string | null
  source_commit_author_login: string | null
  source_commit_author_avatar_url: string | null
  is_network_fork: boolean
  /**
   * Marks the viewed repository's default branch, which the endpoint sends without an ahead/behind
   * comparison. Absent on a response that predates that backend change, in which case the row
   * falls back to rendering whatever comparison it did send.
   */
  is_default_branch?: boolean
}

interface NetworkTableApiResponse {
  rows: NetworkTableApiRow[]
  page_count: number
}

export interface NetworkTableData {
  rows: NetworkGraphTableRow[]
  pageCount: number
}

function isCommitCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
}

/**
 * Narrows the endpoint's integer array to the `[ahead, behind]` pair the table renders. The
 * parameter is `unknown` rather than the declared row type because this is the one field whose
 * shape changed between endpoint versions, so it is validated against the parsed JSON rather than
 * trusted: anything that is not exactly two non-negative integers degrades to `null`, which keeps
 * a malformed row to a single placeholder cell instead of throwing out of the whole fetch.
 */
function toAheadBehind(value: unknown): AheadBehind | null {
  if (!Array.isArray(value) || value.length !== 2) {
    return null
  }
  const [ahead, behind] = value
  if (!isCommitCount(ahead) || !isCommitCount(behind)) {
    return null
  }
  return [ahead, behind]
}

function mapRow(apiRow: NetworkTableApiRow, index: number): NetworkGraphTableRow {
  return {
    id: index,
    repo: apiRow.repo,
    branch: apiRow.branch,
    isDefaultBranch: apiRow.is_default_branch ?? false,
    aheadBehind: toAheadBehind(apiRow.ahead_behind),
    aheadBehindDescription: apiRow.ahead_behind_description,
    aheadPath: apiRow.ahead_path ?? null,
    behindPath: apiRow.behind_path ?? null,
    commit: apiRow.commit,
    commitDate: apiRow.commit_date,
    commitDescription: apiRow.commit_description,
    commitAuthor: apiRow.commit_author,
    commitAuthorLogin: apiRow.commit_author_login,
    commitAuthorAvatarUrl: apiRow.commit_author_avatar_url,
    sourceRepo: apiRow.source_repo,
    sourceBranch: apiRow.source_branch,
    sourceCommit: apiRow.source_commit,
    sourceCommitDate: apiRow.source_date,
    sourceCommitDescription: apiRow.source_commit_description,
    sourceCommitAuthor: apiRow.source_commit_author,
    sourceCommitAuthorLogin: apiRow.source_commit_author_login,
    sourceCommitAuthorAvatarUrl: apiRow.source_commit_author_avatar_url,
    isNetworkFork: apiRow.is_network_fork,
  }
}

/**
 * Fetches the network graph table data. The endpoint returns `202` while the graph is still being
 * built and `200` with the rows once it is ready; `fetchPoll` transparently polls the `202` until
 * the data is available (mirroring how the canvas graph loads its meta data).
 */
export async function fetchNetworkTableData(url: string, page: number): Promise<NetworkTableData> {
  const requestUrl = new URL(url, window.location.origin)
  requestUrl.searchParams.set('page', String(page))
  const response = await fetchPoll(requestUrl.toString(), {
    headers: {
      accept: 'application/json',
    },
  })
  if (!response.ok) {
    throw new Error(`Failed to load network graph table data (${response.status})`)
  }
  const data: NetworkTableApiResponse = await response.json()
  return {
    rows: data.rows.map(mapRow),
    pageCount: data.page_count,
  }
}
